'use client';

import { AlertTriangleIcon, ChevronDownIcon, ChevronRightIcon, CompassIcon, NavigationIcon, UploadIcon, XIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { NavMeshGenerationOptions, ProgressCallback } from '@/editor/core/actions/navmeshActions';
import type { NavigationSettings } from '@gnsx/genesys.js';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { clearNavMesh, generateNavMesh, getNavMeshSettings, importNavMesh, setNavMeshSettingsPropertyValue } from '@/editor/core/actions/navmeshActions';
import { useNavMeshState } from '@/editor/hooks/useNavMeshState';
interface NavMeshDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}
interface NavMeshNumberFieldProps {
  id: string;
  label: string;
  field: keyof NavMeshGenerationOptions;
  settings: NavigationSettings;
  min?: string;
  max?: string;
  step?: string;
  parseAs?: 'float' | 'int';
}
const NavMeshNumberField = observer(function NavMeshNumberField({
  id,
  label,
  field,
  settings,
  min,
  max,
  step,
  parseAs = 'float'
}: NavMeshNumberFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const committed = settings[field] as number;
  useEffect(() => {
    setDraft(null);
  }, [committed]);
  const commitValue = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setDraft(null);
      return;
    }
    const parsed = parseAs === 'int' ? parseInt(trimmed, 10) : parseFloat(trimmed);
    if (!Number.isNaN(parsed)) {
      setNavMeshSettingsPropertyValue(field, parsed);
    }
    setDraft(null);
  }, [field, parseAs]);
  return <div className="space-y-2">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} type="number" value={draft ?? committed} onChange={e => setDraft(e.target.value)} onBlur={e => commitValue(e.target.value)} onKeyDown={e => {
      if (e.key === 'Enter') {
        commitValue(e.currentTarget.value);
        e.currentTarget.blur();
      }
    }} min={min} max={max} step={step} className="h-8" />
    </div>;
});
NavMeshNumberField.displayName = 'NavMeshNumberField';
export const NavMeshDialog = observer(function NavMeshDialog({
  isOpen,
  onOpenChange
}: NavMeshDialogProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const navmeshFileRef = useRef<HTMLInputElement>(null);
  const settings = getNavMeshSettings();

  // Use reactive NavMesh state instead of static function calls
  const navMeshState = useNavMeshState();
  const {
    forceUpdate,
    ...navMeshInfo
  } = navMeshState;
  const isAvailable = navMeshState.available;
  const handleGenerateAndClose = useCallback(async () => {
    // Close dialog immediately
    onOpenChange(false);

    // Create a single persistent toast for the entire process
    const progressToastId = toast.loading('Initializing NavMesh generation...', {
      duration: Infinity
    });
    const progressCallback: ProgressCallback = (stage: string, progressValue: number, message?: string) => {
      const progressMessage = `${stage}: ${Math.round(progressValue)}%${message ? ` - ${message}` : ''}`;

      // Update the existing toast instead of creating new ones
      toast.loading(progressMessage, {
        id: progressToastId,
        duration: Infinity
      });
    };
    try {
      const success = await generateNavMesh(progressCallback, settings);
      if (success) {
        // The generateNavMesh function already handles export automatically
        toast.success('NavMesh generated and saved successfully!', {
          id: progressToastId,
          duration: 4000
        });
        // Force immediate state update after generation
        forceUpdate();
      } else {
        toast.error('Failed to generate NavMesh', {
          id: progressToastId,
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error generating NavMesh:', error);
      toast.error('Error occurred while generating NavMesh', {
        id: progressToastId,
        duration: 5000
      });
    }
  }, [settings, onOpenChange, forceUpdate]);
  const handleImport = useCallback(async () => {
    const navmeshFile = navmeshFileRef.current?.files?.[0];
    if (!navmeshFile) {
      toast.error('Please select a NavMesh file', {
        duration: 4000
      });
      return;
    }
    setIsImporting(true);
    try {
      const success = await importNavMesh(navmeshFile);
      if (success) {
        toast.success('NavMesh imported successfully!');
        // Clear file input
        if (navmeshFileRef.current) navmeshFileRef.current.value = '';
        // Force immediate state update after import
        forceUpdate();
      }
    } catch (error) {
      console.error('Error importing NavMesh:', error);
      toast.error('Failed to import NavMesh', {
        duration: 5000
      });
    } finally {
      setIsImporting(false);
    }
  }, [forceUpdate]);
  const handleClear = useCallback(async () => {
    try {
      await clearNavMesh();
      // Force immediate state update after clearing
      forceUpdate();
    } catch (error) {
      console.error('Error clearing NavMesh:', error);
      toast.error('Failed to clear NavMesh', {
        duration: 5000
      });
    }
  }, [forceUpdate]);

  // Show unavailable dialog
  if (!isAvailable) {
    return <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangleIcon className="h-5 w-5 text-destructive" />
              NavMesh System Unavailable
            </DialogTitle>
            <DialogDescription>
              NavMesh functionality is not available in the current engine version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 px-6 pb-6">
            <div className="text-xs bg-muted p-3 rounded-md">
              <div className="font-medium mb-1">Possible reasons:</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Engine version (genesys.js) may not include navigation server</li>
                <li>Navigation system might need to be explicitly enabled in world configuration</li>
                <li>Feature may be available in a newer engine version</li>
              </ul>
            </div>
            <div className="text-xs text-muted-foreground">
              Check the console for more detailed debugging information.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>;
  }
  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <NavigationIcon className="h-5 w-5 text-blue-500" />
            NavMesh
          </DialogTitle>
          <DialogDescription>
            Generate, import, or clear the scene navmesh.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border px-3 py-2 flex items-center justify-between">
            <div className="text-sm">
              <span className="text-muted-foreground mr-2">Status:</span>
              <span className={navMeshInfo.hasNavMesh ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                {navMeshInfo.hasNavMesh ? 'NavMesh Present' : 'No NavMesh'}
              </span>
            </div>
            {navMeshInfo.hasNavMesh && <Button onClick={handleClear} variant="destructive" size="sm">
                <XIcon className="h-4 w-4 mr-2" />
                Clear
              </Button>}
          </div>

          <section className="rounded-md border p-4 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold">Generate</h3>
                <p className="text-xs text-muted-foreground">Tune agent and voxel settings, then generate and save.</p>
              </div>
              <Button onClick={handleGenerateAndClose}>
                <CompassIcon className="h-4 w-4 mr-2" />
                Generate & Save
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <NavMeshNumberField id="cellSize" label="Cell Size" field="cellSize" settings={settings} min="0.05" max="2.0" step="0.05" />
              <NavMeshNumberField id="cellHeight" label="Cell Height" field="cellHeight" settings={settings} min="0.05" max="2.0" step="0.05" />
              <NavMeshNumberField id="walkableRadius" label="Agent Radius" field="walkableRadius" settings={settings} min="0.1" max="5.0" step="0.1" />
              <NavMeshNumberField id="walkableHeight" label="Agent Height" field="walkableHeight" settings={settings} min="0.5" max="10.0" step="0.1" />
              <NavMeshNumberField id="walkableClimb" label="Max Step Height" field="walkableClimb" settings={settings} min="0.0" max="2.0" step="0.1" />
              <NavMeshNumberField id="walkableSlopeAngle" label="Max Slope (degrees)" field="walkableSlopeAngle" settings={settings} min="0" max="90" step="1" />
            </div>

            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground w-full">
                {showAdvanced ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                  Advanced Settings
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 mt-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <NavMeshNumberField id="maxEdgeLength" label="Max Edge Length" field="maxEdgeLength" settings={settings} min="1" max="50" step="1" />
                  <NavMeshNumberField id="maxSimplificationError" label="Max Simplification Error" field="maxSimplificationError" settings={settings} min="0.1" max="5.0" step="0.1" />
                  <NavMeshNumberField id="minRegionArea" label="Min Region Area" field="minRegionArea" settings={settings} min="1" max="100" step="1" parseAs="int" />
                  <NavMeshNumberField id="mergeRegionArea" label="Merge Region Area" field="mergeRegionArea" settings={settings} min="1" max="200" step="1" parseAs="int" />
                  <NavMeshNumberField id="maxVertsPerPoly" label="Max Verts Per Polygon" field="maxVertsPerPoly" settings={settings} min="3" max="8" step="1" parseAs="int" />
                  <NavMeshNumberField id="detailSampleDist" label="Detail Sample Distance" field="detailSampleDist" settings={settings} min="1" max="20" step="1" />
                  <NavMeshNumberField id="detailSampleMaxError" label="Detail Sample Max Error" field="detailSampleMaxError" settings={settings} min="0.1" max="10.0" step="0.1" />
                  <NavMeshNumberField id="borderSize" label="Border Size" field="borderSize" settings={settings} min="0" max="10" step="1" parseAs="int" />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </section>

          <section className="rounded-md border p-4 space-y-3">
            <div>
              <h3 className="text-sm font-semibold">Import</h3>
              <p className="text-xs text-muted-foreground">Load a prebuilt `.navmesh` file into the current scene.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-2">
                <Label htmlFor="navmesh-file" className="text-xs">NavMesh File</Label>
                <Input id="navmesh-file" ref={navmeshFileRef} type="file" accept=".navmesh" className="text-xs" />
              </div>
              <Button onClick={handleImport} disabled={isImporting} variant="outline" size="sm" className="md:min-w-40">
                <UploadIcon className="h-4 w-4 mr-2" />
                {isImporting ? 'Importing...' : 'Import NavMesh'}
              </Button>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
});
NavMeshDialog.displayName = 'NavMeshDialog';